import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { cn } from '../utils/cn'
import { Button } from './button'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

export function MagicText({
  initialValue = '',
  mode = 'double',
  disabled = false,
  ...props
}: Props): JSX.Element {
  const [editingValue, setEditing] = useState<false | string>(false)
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<HTMLSpanElement>(null)
  const [focused, setFocus] = useState(false)
  const handleSubmit = useCallback(() => {
    const value = inputRef.current?.textContent || ''
    if (value.trim() !== initialValue) {
      props.onCommit(value.trim())
      if (inputRef.current && value === '')
        inputRef.current.innerHTML = `<span className="text-input-secondary-foreground/70">${props.placeholder}</span>`
    }
    setEditing(false)
    setFocus(false)
  }, [props.onCommit, initialValue])

  const handleCancel = useCallback(() => {
    setEditing(false)
    setFocus(false)
    if (inputRef.current)
      inputRef.current.innerHTML =
        initialValue ||
        `<span className="text-input-secondary-foreground/70">${props.placeholder}</span>`
  }, [initialValue, props.placeholder])

  const handleEdit = useCallback(() => {
    if (editingValue !== false || disabled) return
    setEditing(initialValue || '')
    setFocus(true)
    if (inputRef.current) {
      inputRef.current.innerHTML =
        initialValue ||
        `<span className="text-input-secondary-foreground/70">${props.placeholder}</span>`

      const range = document.createRange()
      range.selectNodeContents(inputRef.current)
      const selection = window.getSelection()

      selection?.removeAllRanges()
      selection?.addRange(range)
      setTimeout(() => inputRef.current?.focus(), 150)
    }
  }, [initialValue, editingValue, disabled])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        handleCancel()
      }
    }
    if (focused === true) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [focused])

  useEffect(() => {
    if (props.autoFocus) {
      handleEdit()
    }
  }, [props.autoFocus])

  const isEditing = editingValue !== false

  return (
    <span
      ref={wrapperRef}
      className="group relative inline-flex items-center gap-2"
      {...{ [mode === 'single' ? 'onClick' : 'onDoubleClick']: handleEdit }}
    >
      <span className="relative shrink grow">
        {!isEditing && (
          <span className="bg-input-background absolute -top-1 -right-1 -bottom-1 -left-1 rounded-md opacity-0 group-hover:opacity-100" />
        )}
        <span
          ref={inputRef}
          className={cn(
            'relative -m-1 inline-block h-auto w-fit min-w-20 border border-transparent p-1',
            {
              'text-foreground bg-input-secondary-background focus-visible:border-input-focus-border focus-visible:bg-input-background rounded-md focus-visible:border focus-visible:shadow-[0px_0px_0px_3px_hsl(var(--input-focus-shadow))] focus-visible:outline-hidden':
                isEditing,
            },
          )}
          contentEditable={isEditing}
          onKeyUp={(e) => {
            if (e.key === 'Escape') {
              handleCancel()
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && props.multiline !== true) {
              handleSubmit()
            }
          }}
          onPaste={(e) => {
            e.preventDefault()
            const text = e.clipboardData.getData('text/plain')
            if (inputRef.current) inputRef.current.innerText = text
          }}
          suppressContentEditableWarning
        >
          {initialValue || (!isEditing && props.placeholder)}
        </span>
      </span>
      <span className={cn('flex shrink-0 items-center gap-1', { hidden: disabled })}>
        {isEditing ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="submit"
                  icon="check"
                  size="icon-sm"
                  variant="secondary"
                  onClick={handleSubmit}
                />
              </TooltipTrigger>
              <TooltipContent>Save changes</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  icon="arrow-u-up-left"
                  size="icon-sm"
                  variant="secondary"
                  onClick={handleCancel}
                />
              </TooltipTrigger>
              <TooltipContent>Cancel changes</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <Button
            id={props.id}
            icon="pencil-simple"
            size="icon-sm"
            variant="secondary"
            className="shrink-0 opacity-0 group-hover:opacity-100"
            onClick={handleEdit}
          />
        )}
      </span>
    </span>
  )
}

interface Props {
  onCommit: (value: string) => void
  initialValue?: string | null
  placeholder?: string
  multiline?: boolean
  id?: string
  mode?: 'single' | 'double'
  autoFocus?: boolean
  disabled?: boolean
}
